// The one place the web renders times (service-contract §4.10; qa-mission-control
// docs/time-and-timezones.md §4.1). Everything goes through an explicit IANA zone, the
// viewer's, and carries its abbreviation, never the implicit locale of the browser's
// `toLocale*String`. ESLint bans those calls everywhere else. Ported from Mission
// Control's web/src/lib/time.ts. Run-ledger has no user profile, so the viewer's zone is
// the browser's.

/** The viewer's IANA zone, sent as ``tz`` with date-only bounds (service-contract §4.10). */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Adds a ``created_after`` / ``created_before`` range from two ``<input type="date">``
 * values. The dates go as they are, with the viewer's ``tz``: the API cuts the days in
 * that zone, and a ``created_before`` date includes that whole day. Never pass a date to
 * ``new Date()``, which reads it as UTC midnight.
 */
export function applyCreatedRange(params: URLSearchParams, after: string, before: string): void {
  if (after) params.set("created_after", after);
  if (before) params.set("created_before", before);
  if (after || before) params.set("tz", viewerTimeZone());
}

export type InstantStyle =
  /** "Sep 28, 10:11 AM MDT": table cells. */
  | "compact"
  /** "Sep 28, 2026, 10:11:07 AM MDT": detail views. */
  | "full"
  /** "10:11:07 AM MDT": a live feed, where the day is today. */
  | "time"
  /** "Sep 28, 2026": the calendar day the instant falls on in `tz`. */
  | "date";

const OPTIONS: Record<InstantStyle, Intl.DateTimeFormatOptions> = {
  compact: { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" },
  full: {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  },
  time: { hour: "numeric", minute: "2-digit", second: "2-digit", timeZoneName: "short" },
  date: { year: "numeric", month: "short", day: "numeric" },
};

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string, style: InstantStyle): Intl.DateTimeFormat {
  const key = `${style}|${tz}`;
  let f = formatters.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat("en-US", { ...OPTIONS[style], timeZone: tz });
    } catch {
      // An unknown zone name must not take the page down; the browser zone is always valid.
      f = new Intl.DateTimeFormat("en-US", { ...OPTIONS[style], timeZone: viewerTimeZone() });
    }
    formatters.set(key, f);
  }
  return f;
}

/** Render an instant (ISO 8601 with an offset or `Z`) in `tz` (default: the viewer's),
 *  labelled with the zone abbreviation. An unparseable value comes back as-is. */
export function formatInstant(
  iso: string,
  { tz = viewerTimeZone(), style = "compact" }: { tz?: string; style?: InstantStyle } = {},
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return formatter(tz, style).format(d);
}

/** The exact instant in UTC ("2026-09-28T16:11:07Z"), for `title` tooltips. */
export function formatUtcInstant(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().slice(0, 19)}Z`;
}

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const calendarFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

/** Render a calendar date ("2026-09-28") as "Sep 28, 2026". A date has no zone, so it is
 *  built from its parts and formatted in UTC: `new Date("YYYY-MM-DD")` would show the
 *  previous day anywhere west of UTC. */
export function formatCalendarDate(yyyyMmDd: string): string {
  const m = CALENDAR_DATE.exec(yyyyMmDd);
  if (!m) return yyyyMmDd;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  // Date.UTC rolls 2026-02-31 into March; reject rather than show a wrong day.
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) {
    return yyyyMmDd;
  }
  return calendarFormatter.format(date);
}

const dayKeyFormatters = new Map<string, Intl.DateTimeFormat>();

/** The calendar day an instant falls on in `tz`, as a day number (days since 1970-01-01). */
function dayNumber(d: Date, tz: string): number {
  let f = dayKeyFormatters.get(tz);
  if (!f) {
    // en-CA formats as YYYY-MM-DD.
    f = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: tz });
    dayKeyFormatters.set(tz, f);
  }
  const [y, m, day] = f.format(d).split("-").map(Number);
  return Date.UTC(y, m - 1, day) / 86_400_000;
}

/** How many calendar days ago `iso` was in `tz` (default: the viewer's): 0 today,
 *  1 yesterday. Counts midnights, not 24-hour periods, so 23:00 yesterday is 1 day ago. */
export function calendarDaysAgo(
  iso: string | null | undefined,
  { tz = viewerTimeZone(), now = new Date() }: { tz?: string; now?: Date } = {},
): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return dayNumber(now, tz) - dayNumber(d, tz);
}
